import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Fade from '@mui/material/Fade';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';

const TabPanel = ({ value, index, children }) => {
  return value === index && <Box sx={{ mt: 1 }}>{children}</Box>;
};

TabPanel.propTypes = {
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  children: PropTypes.node,
};

const ConnectionTable = ({ rows }) => (
  <TableContainer
    component={Paper}
    sx={{
      borderRadius: 1,
      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
      border: '1px solid #E5E7EB',
    }}
  >
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
          {['Entity Name', 'Connector Type', 'Connection Name', 'Field Count'].map((h) => (
            <TableCell
              key={h}
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                color: '#374151',
                py: 0.8,
              }}
            >
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.isArray(rows) && rows.length > 0 ? (
          rows.map((r, idx) => (
            <TableRow
              key={idx}
              sx={{
                '&:hover': { backgroundColor: '#F9FAFB' },
                '&:last-child td': { borderBottom: 0 },
              }}
            >
              <TableCell sx={{ fontSize: '0.75rem', color: '#374151', py: 0.8 }}>
                {r?.entity_name ?? '—'}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: '#374151', py: 0.8 }}>
                {r?.connector_type ?? '—'}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: '#374151', py: 0.8 }}>
                {r?.connection_name ?? '—'}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: '#374151', py: 0.8 }}>
                {r?.field_count ?? '—'}
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={4}
              sx={{
                color: '#9CA3AF',
                fontSize: '0.8rem',
                textAlign: 'center',
                py: 2,
                fontStyle: 'italic',
              }}
            >
              No data found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

ConnectionTable.propTypes = {
  rows: PropTypes.array.isRequired,
};

const WorkflowConnectionsDialog = ({ open, onClose, loading, error, data, workflowName }) => {
  const summary = data || {};
  const sources = summary?.connections?.sources || [];
  const destinations = summary?.connections?.destinations || [];
  const [tab, setTab] = useState(0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={200}
      keepMounted
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          color: '#111827',
          fontSize: '1rem',
          pb: 1,
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        Workflow Connections
        {workflowName ? ` — ${workflowName}` : ''}
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: '#FAFAFA', p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={30} />
          </Box>
        ) : error ? (
          <Typography sx={{ color: '#EF4444', textAlign: 'center', py: 2 }}>
            {error}
          </Typography>
        ) : (
          <>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="fullWidth"
              sx={{
                mb: 1.5,
                minHeight: '32px',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  minHeight: '32px',
                },
              }}
            >
              <Tab label={`Sources (${sources.length})`} />
              <Tab label={`Destinations (${destinations.length})`} />
            </Tabs>

            <TabPanel value={tab} index={0}>
              <ConnectionTable rows={sources} />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <ConnectionTable rows={destinations} />
            </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 1.5, borderTop: '1px solid #E5E7EB' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            px: 2.5,
            py: 0.6,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.8rem',
            fontWeight: 500,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

WorkflowConnectionsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  data: PropTypes.object,
  workflowName: PropTypes.string,
};

export default WorkflowConnectionsDialog;
